"use client";

import { ChangeEvent, useEffect, useRef, useState } from "react";
import { Download, ImagePlus, Sparkles } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ClearButton } from "@/components/tools/clear-button";
import { CopyButton } from "@/components/tools/copy-button";
import { ErrorMessage } from "@/components/tools/error-message";
import { InputOutputPanel } from "@/components/tools/input-output-panel";
import { CodeOutput } from "@/components/tools/tool-output";
import { formatFileSize } from "@/lib/tools/pdf";

type ImageState = {
  file: File | null;
  width: number;
  height: number;
  quality: number;
  format: "image/jpeg" | "image/png" | "image/webp";
};

type ImageOutput = {
  url: string;
  dataUrl?: string;
  fileName: string;
  width: number;
  height: number;
  byteSize: number;
  sourceByteSize: number;
  format: ImageState["format"];
  notes?: string[];
};

type SvgDimensions = {
  width: number;
  height: number;
};

type SvgPrepareResult =
  | {
      ok: true;
      value: {
        svgText: string;
        dimensions: SvgDimensions;
        hasMissingClassStyles: boolean;
        notes: string[];
      };
    }
  | {
      ok: false;
      error: string;
    };

const imageFormatLabels: Record<ImageState["format"], string> = {
  "image/jpeg": "JPEG",
  "image/png": "PNG",
  "image/webp": "WebP",
};

const imageFormatExtensions: Record<ImageState["format"], string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

const sampleSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512">
  <rect width="512" height="512" rx="48" fill="#0f172a"/>
  <circle cx="136" cy="140" r="58" fill="#22c55e"/>
  <path d="M105 328c70-88 138-88 204 0 32-40 64-60 96-60 23 0 45 10 67 30v118H40v-16c18-24 40-48 65-72z" fill="#38bdf8"/>
  <text x="256" y="238" text-anchor="middle" font-family="Arial, sans-serif" font-size="86" font-weight="700" fill="#f8fafc">SVG</text>
</svg>`;

const maxExportPixels = 16_000_000;

function canvasToBlob(canvas: HTMLCanvasElement, type: ImageState["format"], quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error("This browser could not export the image."));
      },
      type,
      quality / 100,
    );
  });
}

function loadImage(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not read that image file."));
    };

    image.src = objectUrl;
  });
}

function loadSvgImage(svgText: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const blob = new Blob([svgText], { type: "image/svg+xml;charset=utf-8" });
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not render that SVG."));
    };

    image.src = objectUrl;
  });
}

function blobToDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not prepare the image data URL."));
    reader.readAsDataURL(blob);
  });
}

function outputFileName(file: File, suffix: string, format: ImageState["format"]) {
  const baseName = file.name.replace(/\.[^.]+$/, "") || "image";

  return `${baseName}-${suffix}.${imageFormatExtensions[format]}`;
}

function svgOutputFileName(sourceName: string, format: ImageState["format"]) {
  const baseName = sourceName.replace(/\.[^.]+$/, "") || "svg-image";

  return `${baseName}.${imageFormatExtensions[format]}`;
}

function parseSvgLength(value: string | null) {
  if (!value) {
    return null;
  }

  const match = value.trim().match(/^([+-]?\d*\.?\d+)(px|pt|pc|mm|cm|in)?$/i);

  if (!match) {
    return null;
  }

  const numericValue = Number(match[1]);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }

  const unit = match[2]?.toLowerCase();

  switch (unit) {
    case "in":
      return numericValue * 96;
    case "cm":
      return numericValue * (96 / 2.54);
    case "mm":
      return numericValue * (96 / 25.4);
    case "pt":
      return numericValue * (96 / 72);
    case "pc":
      return numericValue * 16;
    default:
      return numericValue;
  }
}

function parseSvgViewBox(value: string | null) {
  if (!value) {
    return null;
  }

  const parts = value
    .trim()
    .split(/[\s,]+/)
    .map(Number);

  if (parts.length !== 4 || parts.some((part) => !Number.isFinite(part))) {
    return null;
  }

  const [, , width, height] = parts;

  if (width <= 0 || height <= 0) {
    return null;
  }

  return { width, height };
}

function getSvgDimensions(svgElement: Element): SvgDimensions {
  const width = parseSvgLength(svgElement.getAttribute("width"));
  const height = parseSvgLength(svgElement.getAttribute("height"));
  const viewBox = parseSvgViewBox(svgElement.getAttribute("viewBox"));

  return {
    width: Math.round(width ?? viewBox?.width ?? 512),
    height: Math.round(height ?? viewBox?.height ?? 512),
  };
}

function hasExternalOrScriptReference(value: string) {
  const normalized = value.trim().toLowerCase();

  return (
    normalized.startsWith("javascript:") ||
    normalized.includes("@import") ||
    /url\(\s*["']?(?:https?:)?\/\//i.test(value) ||
    /(?:^|\s)(?:https?:)?\/\//i.test(value)
  );
}

function hasInlinePaintOrStyle(element: Element) {
  return (
    element.hasAttribute("fill") ||
    element.hasAttribute("stroke") ||
    element.hasAttribute("style")
  );
}

function hasEmbeddedStyle(svgElement: Element) {
  return Boolean(svgElement.querySelector("style"));
}

function prepareSvg(svgText: string, cssText = ""): SvgPrepareResult {
  if (!svgText.trim()) {
    return { ok: false, error: "Paste SVG markup or choose an SVG file first." };
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(svgText, "image/svg+xml");
  const parserError = document.querySelector("parsererror");

  if (parserError) {
    return { ok: false, error: "That SVG could not be parsed. Check the markup and try again." };
  }

  const svgElement = document.documentElement;

  if (svgElement.nodeName.toLowerCase() !== "svg") {
    return { ok: false, error: "The source must start with an SVG element." };
  }

  if (cssText.trim() && hasExternalOrScriptReference(cssText)) {
    return {
      ok: false,
      error:
        "External URLs and imports in SVG CSS are blocked so the SVG can be converted locally.",
    };
  }

  const notes: string[] = [];
  const blockedElementNames = new Set(["script", "iframe", "object", "embed", "link"]);
  let removedElementCount = 0;
  let removedAttributeCount = 0;
  let classedShapeCount = 0;
  let classedShapeWithoutPaintCount = 0;
  const shapeSelector = "circle,ellipse,line,path,polygon,polyline,rect,text,tspan";

  const allElements = Array.from(document.querySelectorAll("*"));

  for (const element of allElements) {
    if (blockedElementNames.has(element.nodeName.toLowerCase())) {
      element.remove();
      removedElementCount += 1;
      continue;
    }

    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.name.toLowerCase();

      if (name.startsWith("xmlns")) {
        continue;
      }

      if (name.startsWith("on")) {
        element.removeAttribute(attribute.name);
        removedAttributeCount += 1;
        continue;
      }

      if (hasExternalOrScriptReference(attribute.value)) {
        return {
          ok: false,
          error:
            "External URLs and script references are blocked so the SVG can be converted locally.",
        };
      }
    }

    if (element.matches(shapeSelector) && element.hasAttribute("class")) {
      classedShapeCount += 1;

      if (!hasInlinePaintOrStyle(element)) {
        classedShapeWithoutPaintCount += 1;
      }
    }
  }

  for (const styleElement of Array.from(document.querySelectorAll("style"))) {
    if (styleElement.textContent && hasExternalOrScriptReference(styleElement.textContent)) {
      return {
        ok: false,
        error:
          "External URLs and imports in SVG styles are blocked so the SVG can be converted locally.",
      };
    }
  }

  if (!svgElement.getAttribute("xmlns")) {
    svgElement.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  }

  if (cssText.trim()) {
    const styleElement = document.createElementNS("http://www.w3.org/2000/svg", "style");
    styleElement.textContent = cssText;
    svgElement.insertBefore(styleElement, svgElement.firstChild);
  }

  const hasMissingClassStyles =
    classedShapeCount > 0 &&
    classedShapeWithoutPaintCount > 0 &&
    !cssText.trim() &&
    !hasEmbeddedStyle(svgElement);

  if (hasMissingClassStyles) {
    notes.push(
      "This SVG uses class names without embedded CSS, so unstyled shapes may render with browser-default black fills.",
    );
  }

  if (removedElementCount > 0) {
    notes.push(`Removed ${removedElementCount} unsupported element(s) before rendering.`);
  }

  if (removedAttributeCount > 0) {
    notes.push(`Removed ${removedAttributeCount} event handler attribute(s) before rendering.`);
  }

  return {
    ok: true,
    value: {
      svgText: new XMLSerializer().serializeToString(svgElement),
      dimensions: getSvgDimensions(svgElement),
      hasMissingClassStyles,
      notes,
    },
  };
}

function ImageOutputPanel({ output }: { output: ImageOutput | null }) {
  if (!output) {
    return <CodeOutput placeholder="Processed image details will appear here." />;
  }

  const delta = output.byteSize - output.sourceByteSize;

  return (
    <div className="space-y-4">
      <div
        className="min-h-80 rounded-md border border-zinc-200 bg-zinc-100 bg-contain bg-center bg-no-repeat"
        style={{ backgroundImage: `url("${output.url}")` }}
        aria-label="Processed image preview"
      />
      {output.notes?.length ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {output.notes.join(" ")}
        </div>
      ) : null}
      <div className="overflow-hidden rounded-md border border-zinc-200">
        <table className="w-full text-left text-sm">
          <tbody>
            <tr className="border-b border-zinc-100">
              <th className="w-36 bg-zinc-50 px-3 py-2 font-medium text-zinc-600">File</th>
              <td className="break-all px-3 py-2 font-mono text-zinc-950">{output.fileName}</td>
            </tr>
            <tr className="border-b border-zinc-100">
              <th className="bg-zinc-50 px-3 py-2 font-medium text-zinc-600">Dimensions</th>
              <td className="px-3 py-2 font-mono text-zinc-950">
                {output.width} x {output.height}
              </td>
            </tr>
            <tr className="border-b border-zinc-100">
              <th className="bg-zinc-50 px-3 py-2 font-medium text-zinc-600">Format</th>
              <td className="px-3 py-2 font-mono text-zinc-950">
                {imageFormatLabels[output.format]}
              </td>
            </tr>
            <tr>
              <th className="bg-zinc-50 px-3 py-2 font-medium text-zinc-600">Size</th>
              <td className="px-3 py-2 font-mono text-zinc-950">
                {formatFileSize(output.byteSize)}
                <span className="ml-2 text-zinc-500">
                  ({delta <= 0 ? "-" : "+"}
                  {formatFileSize(Math.abs(delta))})
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SvgToImageTool() {
  const [svgText, setSvgText] = useState(sampleSvg);
  const [cssText, setCssText] = useState("");
  const [sourceName, setSourceName] = useState("localtools-svg.svg");
  const [sourceByteSize, setSourceByteSize] = useState(sampleSvg.length);
  const [format, setFormat] = useState<ImageState["format"]>("image/png");
  const [quality, setQuality] = useState(92);
  const [width, setWidth] = useState(512);
  const [height, setHeight] = useState(512);
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [inputKey, setInputKey] = useState(0);
  const [output, setOutput] = useState<ImageOutput | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const objectUrlRef = useRef("");

  useEffect(
    () => () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    },
    [],
  );

  const clearOutput = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = "";
    }

    setOutput(null);
  };

  const onSvgFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    if (!file) {
      return;
    }

    setError("");
    clearOutput();

    try {
      const text = await file.text();
      const prepared = prepareSvg(text, cssText);
      setSvgText(text);
      setSourceName(file.name || "uploaded-svg.svg");
      setSourceByteSize(file.size);

      if (prepared.ok) {
        setWidth(prepared.value.dimensions.width);
        setHeight(prepared.value.dimensions.height);
      }
    } catch {
      setError("Could not read that SVG file.");
    }
  };

  const onSvgTextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setSvgText(event.target.value);
    setSourceName("pasted-svg.svg");
    setSourceByteSize(0);
    setError("");
    clearOutput();
  };

  const onCssTextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setCssText(event.target.value);
    setError("");
    clearOutput();
  };

  const useSvgSize = () => {
    const prepared = prepareSvg(svgText);

    if (!prepared.ok) {
      setError(prepared.error);
      return;
    }

    setWidth(prepared.value.dimensions.width);
    setHeight(prepared.value.dimensions.height);
    setError("");
  };

  const clear = () => {
    setSvgText("");
    setCssText("");
    setSourceName("pasted-svg.svg");
    setSourceByteSize(0);
    setFormat("image/png");
    setQuality(92);
    setWidth(512);
    setHeight(512);
    setBackgroundColor("#ffffff");
    setInputKey((value) => value + 1);
    setError("");
    clearOutput();
  };

  const convert = async () => {
    const prepared = prepareSvg(svgText, cssText);

    if (!prepared.ok) {
      setError(prepared.error);
      clearOutput();
      return;
    }

    const exportWidth = Math.max(1, Math.trunc(width));
    const exportHeight = Math.max(1, Math.trunc(height));

    if (exportWidth * exportHeight > maxExportPixels) {
      setError("Reduce the dimensions before converting. The current canvas is too large.");
      clearOutput();
      return;
    }

    setBusy(true);
    setError("");

    try {
      const image = await loadSvgImage(prepared.value.svgText);
      const canvas = document.createElement("canvas");
      canvas.width = exportWidth;
      canvas.height = exportHeight;
      const context = canvas.getContext("2d");

      if (!context) {
        throw new Error("Canvas rendering is not available in this browser.");
      }

      if (format === "image/jpeg") {
        context.fillStyle = backgroundColor;
        context.fillRect(0, 0, canvas.width, canvas.height);
      }

      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const blob = await canvasToBlob(canvas, format, quality);
      const dataUrl = await blobToDataUrl(blob);
      clearOutput();
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      setOutput({
        url,
        dataUrl,
        fileName: svgOutputFileName(sourceName, format),
        width: canvas.width,
        height: canvas.height,
        byteSize: blob.size,
        sourceByteSize: sourceByteSize || new Blob([svgText]).size,
        format,
        notes: prepared.value.notes,
      });
    } catch (convertError) {
      clearOutput();
      setError(convertError instanceof Error ? convertError.message : "That SVG could not be converted.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <InputOutputPanel
      inputTitle="SVG source"
      outputTitle="Image output"
      inputActions={<ClearButton onClear={clear} />}
      outputActions={
        <>
          <CopyButton value={output?.dataUrl ?? ""} label="Copy data URL" />
          {output ? (
            <a
              href={output.url}
              download={output.fileName}
              className={buttonVariants({ variant: "secondary", size: "sm" })}
            >
              <Download className="h-4 w-4" />
              Download
            </a>
          ) : null}
        </>
      }
      input={
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="svg-file">SVG file</Label>
            <Input
              key={inputKey}
              id="svg-file"
              type="file"
              accept=".svg,image/svg+xml"
              onChange={onSvgFileChange}
            />
            {sourceName ? (
              <p className="text-sm text-zinc-500">
                {sourceName}
                {sourceByteSize ? ` - ${formatFileSize(sourceByteSize)}` : ""}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="svg-source">SVG markup</Label>
            <Textarea
              id="svg-source"
              value={svgText}
              onChange={onSvgTextChange}
              spellCheck={false}
              className="min-h-72"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="svg-css">CSS for SVG classes</Label>
            <Textarea
              id="svg-css"
              value={cssText}
              onChange={onCssTextChange}
              spellCheck={false}
              className="min-h-32"
              placeholder=".icon-part { fill: #0f172a; }"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="svg-format">Format</Label>
              <Select
                id="svg-format"
                value={format}
                onChange={(event) => setFormat(event.target.value as ImageState["format"])}
              >
                <option value="image/png">PNG</option>
                <option value="image/jpeg">JPEG</option>
                <option value="image/webp">WebP</option>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="svg-quality">Quality</Label>
              <Input
                id="svg-quality"
                type="number"
                min={1}
                max={100}
                disabled={format === "image/png"}
                value={quality}
                onChange={(event) => setQuality(Number(event.target.value))}
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="svg-width">Width</Label>
              <Input
                id="svg-width"
                type="number"
                min={1}
                max={8192}
                value={width}
                onChange={(event) => setWidth(Number(event.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="svg-height">Height</Label>
              <Input
                id="svg-height"
                type="number"
                min={1}
                max={8192}
                value={height}
                onChange={(event) => setHeight(Number(event.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="svg-background">Background</Label>
              <Input
                id="svg-background"
                type="color"
                disabled={format !== "image/jpeg"}
                value={backgroundColor}
                onChange={(event) => setBackgroundColor(event.target.value)}
              />
            </div>
          </div>
          <ErrorMessage message={error} />
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={convert} disabled={busy}>
              <ImagePlus className="h-4 w-4" />
              {busy ? "Converting..." : "Convert SVG"}
            </Button>
            <Button type="button" variant="secondary" onClick={useSvgSize}>
              <Sparkles className="h-4 w-4" />
              Use SVG size
            </Button>
          </div>
        </div>
      }
      output={<ImageOutputPanel output={output} />}
    />
  );
}

export function ImageMetadataStripperTool() {
  const [file, setFile] = useState<File | null>(null);
  const [format, setFormat] = useState<ImageState["format"]>("image/jpeg");
  const [quality, setQuality] = useState(92);
  const [inputKey, setInputKey] = useState(0);
  const [output, setOutput] = useState<ImageOutput | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const objectUrlRef = useRef("");

  useEffect(
    () => () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    },
    [],
  );

  const clearOutput = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = "";
    }

    setOutput(null);
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    setFile(nextFile);
    setError("");
    clearOutput();

    if (
      nextFile?.type === "image/jpeg" ||
      nextFile?.type === "image/png" ||
      nextFile?.type === "image/webp"
    ) {
      setFormat(nextFile.type);
    }
  };

  const clear = () => {
    setFile(null);
    setFormat("image/jpeg");
    setQuality(92);
    setInputKey((value) => value + 1);
    setError("");
    clearOutput();
  };

  const stripMetadata = async () => {
    if (!file) {
      setError("Choose an image first.");
      return;
    }

    setBusy(true);
    setError("");

    try {
      const image = await loadImage(file);
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext("2d");

      if (!context) {
        throw new Error("Canvas rendering is not available in this browser.");
      }

      context.drawImage(image, 0, 0);
      const blob = await canvasToBlob(canvas, format, quality);
      clearOutput();
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      setOutput({
        url,
        fileName: outputFileName(file, "clean", format),
        width: canvas.width,
        height: canvas.height,
        byteSize: blob.size,
        sourceByteSize: file.size,
        format,
      });
    } catch (stripError) {
      clearOutput();
      setError(stripError instanceof Error ? stripError.message : "Image metadata could not be removed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <InputOutputPanel
      inputTitle="Image file"
      outputTitle="Processed image"
      inputActions={<ClearButton onClear={clear} />}
      outputActions={
        output ? (
          <a
            href={output.url}
            download={output.fileName}
            className={buttonVariants({ variant: "secondary", size: "sm" })}
          >
            <Download className="h-4 w-4" />
            Download
          </a>
        ) : null
      }
      input={
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="metadata-image-file">Image</Label>
            <Input
              key={inputKey}
              id="metadata-image-file"
              type="file"
              accept="image/*"
              onChange={onFileChange}
            />
            {file ? <p className="text-sm text-zinc-500">{formatFileSize(file.size)}</p> : null}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="metadata-format">Format</Label>
              <Select
                id="metadata-format"
                value={format}
                onChange={(event) => setFormat(event.target.value as ImageState["format"])}
              >
                <option value="image/jpeg">JPEG</option>
                <option value="image/png">PNG</option>
                <option value="image/webp">WebP</option>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="metadata-quality">Quality</Label>
              <Input
                id="metadata-quality"
                type="number"
                min={1}
                max={100}
                disabled={format === "image/png"}
                value={quality}
                onChange={(event) => setQuality(Number(event.target.value))}
              />
            </div>
          </div>
          <ErrorMessage message={error} />
          <Button type="button" onClick={stripMetadata} disabled={busy}>
            <ImagePlus className="h-4 w-4" />
            {busy ? "Processing..." : "Strip metadata"}
          </Button>
        </div>
      }
      output={<ImageOutputPanel output={output} />}
    />
  );
}

export function ImageResizeTool() {
  const [state, setState] = useState<ImageState>({
    file: null,
    width: 800,
    height: 600,
    quality: 85,
    format: "image/jpeg",
  });
  const [outputUrl, setOutputUrl] = useState("");
  const [error, setError] = useState("");

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setOutputUrl("");
    setError("");

    if (!file) {
      setState((current) => ({ ...current, file: null }));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      setState((current) => ({
        ...current,
        file,
        width: image.naturalWidth,
        height: image.naturalHeight,
      }));
      URL.revokeObjectURL(objectUrl);
    };

    image.onerror = () => {
      setError("Could not read that image file.");
      URL.revokeObjectURL(objectUrl);
    };

    image.src = objectUrl;
  };

  const resize = async () => {
    if (!state.file) {
      setError("Choose an image first.");
      return;
    }

    const objectUrl = URL.createObjectURL(state.file);
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.trunc(state.width));
      canvas.height = Math.max(1, Math.trunc(state.height));
      const context = canvas.getContext("2d");

      if (!context) {
        setError("Canvas rendering is not available in this browser.");
        URL.revokeObjectURL(objectUrl);
        return;
      }

      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      setOutputUrl(canvas.toDataURL(state.format, state.quality / 100));
      setError("");
      URL.revokeObjectURL(objectUrl);
    };

    image.onerror = () => {
      setError("Could not resize that image.");
      URL.revokeObjectURL(objectUrl);
    };

    image.src = objectUrl;
  };

  const clear = () => {
    setState({
      file: null,
      width: 800,
      height: 600,
      quality: 85,
      format: "image/jpeg",
    });
    setOutputUrl("");
    setError("");
  };

  return (
    <InputOutputPanel
      inputTitle="Resize settings"
      outputTitle="Resized image"
      inputActions={<ClearButton onClear={clear} />}
      outputActions={
        <>
          <CopyButton value={outputUrl} label="Copy data URL" />
          {outputUrl ? (
            <a
              href={outputUrl}
              download="localtools-resized-image"
              className={buttonVariants({ variant: "secondary", size: "sm" })}
            >
              <Download className="h-4 w-4" />
              Download
            </a>
          ) : null}
        </>
      }
      input={
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="image-file">Image</Label>
            <Input id="image-file" type="file" accept="image/*" onChange={onFileChange} />
            <p className="text-sm text-zinc-500">The file is processed locally with canvas.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="resize-width">Width</Label>
              <Input
                id="resize-width"
                type="number"
                min={1}
                value={state.width}
                onChange={(event) =>
                  setState((current) => ({ ...current, width: Number(event.target.value) }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="resize-height">Height</Label>
              <Input
                id="resize-height"
                type="number"
                min={1}
                value={state.height}
                onChange={(event) =>
                  setState((current) => ({ ...current, height: Number(event.target.value) }))
                }
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="resize-quality">Quality</Label>
              <Input
                id="resize-quality"
                type="number"
                min={1}
                max={100}
                value={state.quality}
                onChange={(event) =>
                  setState((current) => ({ ...current, quality: Number(event.target.value) }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="resize-format">Format</Label>
              <Select
                id="resize-format"
                value={state.format}
                onChange={(event) =>
                  setState((current) => ({
                    ...current,
                    format: event.target.value as ImageState["format"],
                  }))
                }
              >
                <option value="image/jpeg">JPEG</option>
                <option value="image/png">PNG</option>
                <option value="image/webp">WebP</option>
              </Select>
            </div>
          </div>
          <ErrorMessage message={error} />
          <Button type="button" onClick={resize}>
            <Sparkles className="h-4 w-4" />
            Resize image
          </Button>
        </div>
      }
      output={
        outputUrl ? (
          <div
            className="min-h-80 rounded-md border border-zinc-200 bg-zinc-100 bg-contain bg-center bg-no-repeat"
            style={{ backgroundImage: `url("${outputUrl}")` }}
            aria-label="Resized image preview"
          />
        ) : (
          <CodeOutput placeholder="Choose an image and resize it to preview the result." />
        )
      }
    />
  );
}
